import { viewPromotionSchema } from '@integration-layer/schemas/GAEvents/viewPromotion'
import { selectPromotionSchema } from '@integration-layer/schemas/GAEvents/selectPromotion'
import snakeCase from 'lodash/snakeCase'

/**
 * Provides methods for tracking promotion-related events in Google Analytics.
 *
 * @returns {Object} An object containing methods to dispatch promotion events.
 *
 * @description This composable handles tracking of promotion views and selections,
 * converting identifiers to snake_case for consistent tracking.
 *
 * @example
 * const {
 *   dispatchViewPromotionEvent,
 *   dispatchSelectPromotionEvent
 * } = useGAPromotionEvents()
 *
 * dispatchViewPromotionEvent('summer_sale', ['item1', 'item2'])
 * dispatchSelectPromotionEvent('homepage', 'promo123', 'Summer Sale')
 */
export const useGAPromotionEvents = () => {
  // Enable debug mode based on runtime configuration
  const debug = !!useRuntimeConfig().public.ga4.debug

  /**
   * Tracks the view of a promotion.
   *
   * @param {string} promotionId - The unique identifier of the promotion.
   * @param {string[]} items - List of items associated with the promotion.
   * @param {boolean} [waitForDatalayerSetup] - Optional flag to wait for datalayer setup.
   *
   * @description Sends a Google Analytics event when a promotion is viewed,
   * with a snake_case formatted promotion ID.
   */
  const dispatchViewPromotionEvent = (
    promotionId: string,
    items: string[],
    waitForDatalayerSetup?: boolean
  ) =>
    useGASendEvent(
      viewPromotionSchema,
      {
        event: 'view_promotion',
        eventID: '015',
        ecommerce: {
          creative_name: 'EditorialStripAnchors',
          creative_slot: '-',
          promotion_id: snakeCase(`promo-${promotionId}`),
          promotion_name: promotionId,
          items,
        },
      },
      {
        clearEcommerce: true,
        waitForDatalayerSetup,
        debug,
      }
    )

  /**
   * Tracks the selection of a promotion.
   *
   * @param {string} promotionLocation - The location where the promotion was displayed.
   * @param {string} selectedPromotionId - The unique identifier of the selected promotion.
   * @param {string} selectedPromotion - The name of the selected promotion.
   * @param {boolean} [waitForDatalayerSetup] - Optional flag to wait for datalayer setup.
   *
   * @description Sends a Google Analytics event when a promotion is selected,
   * converting both promotion ID and name to snake_case.
   */
  const dispatchSelectPromotionEvent = (
    promotionLocation: string,
    selectedPromotionId: string,
    selectedPromotion: string,
    waitForDatalayerSetup?: boolean
  ) => {
    useGASendEvent(
      selectPromotionSchema,
      {
        event: 'select_promotion',
        eventID: '016',
        ecommerce: {
          creative_name: promotionLocation ?? '',
          creative_slot: '-',
          promotion_id: snakeCase(selectedPromotionId),
          promotion_name: snakeCase(selectedPromotion),
          items: [],
        },
      },
      {
        clearEcommerce: true,
        waitForDatalayerSetup,
        debug,
      }
    )
  }

  return {
    dispatchViewPromotionEvent,
    dispatchSelectPromotionEvent,
  }
}
